import { VisualRandom } from '../core/VisualRandom.js';
import { VISUAL_RECIPE_VERSION, layer, type VisualRecipe } from '../recipe/VisualRecipe.js';
import type { SignalMapping } from '../layers/VisualLayer.js';

/**
 * A recipe chosen by listening, rather than by rolling dice.
 *
 * Rule-based on purpose. The brief is explicit that the architecture matters
 * more than the taste, and it is right: what needs to exist is the path from
 * *audio feature* to *visual configuration*, so that a better set of rules — or
 * a model — can replace the body of this function without anything else moving.
 *
 * The features are averages taken over a stretch of the song rather than one
 * frame, because a recipe chosen from a single frame would be chosen from
 * whatever happened to be playing at the moment the player pressed start.
 */

export interface AudioProfile {
  /** Mean overall loudness, 0..1. */
  readonly amplitude: number;
  readonly bass: number;
  readonly mid: number;
  readonly treble: number;
  /** Onsets per second. */
  readonly onsetRate: number;
  readonly bpm: number;
}

export const QUIET_PROFILE: AudioProfile = {
  amplitude: 0,
  bass: 0,
  mid: 0,
  treble: 0,
  onsetRate: 0,
  bpm: 120,
};

export interface AutoRecipeOptions {
  readonly seed: number;
  readonly profile: AudioProfile;
  readonly available: readonly string[];
  readonly maxLayers?: number;
}

/**
 * The rules, stated plainly so they can be argued with:
 *
 *   loud            -> more layers, higher intensity
 *   quiet           -> fewer layers, the world mostly left alone
 *   bass-heavy      -> RIPPLE, dense and thick
 *   treble-heavy    -> MATH or CODE, because detail suits text
 *   many onsets     -> spawn-driven mappings rather than level-driven ones
 */
export function autoRecipe(options: AutoRecipeOptions): VisualRecipe {
  const { profile, available } = options;
  const random = new VisualRandom(options.seed);
  const cap = options.maxLayers ?? 4;

  const layers = [];
  if (available.includes('MANDALA')) {
    layers.push(
      layer({
        id: 'mandala',
        renderer: 'MANDALA',
        zDepth: 0,
        seed: options.seed,
        // Loud songs push the world harder; quiet ones leave it as authored.
        intensity: 1 + profile.amplitude * 0.35,
        signalMapping: { BASS: { target: 'bloomTrim', scale: 0.3, offset: 0.95 } },
      }),
    );
  }

  // Amplitude decides how much is added at all. A quiet piece gets a quiet
  // treatment rather than the same treatment turned down.
  const budget = profile.amplitude < 0.08 ? 0 : profile.amplitude < 0.2 ? 1 : 2;

  if (budget >= 1 && available.includes('RIPPLE')) {
    const dense = profile.bass > 0.45;
    const spawnDriven = profile.onsetRate > 2.5;
    const mapping: SignalMapping = spawnDriven
      ? { ONSET: { action: 'SPAWN', strength: 0.8 }, NOTE_MISS: { action: 'DISTORT', strength: 1 } }
      : { BASS: { action: 'DENSITY', scale: 0.9 }, NOTE_MISS: { action: 'DISTORT', strength: 1 } };

    layers.push(
      layer({
        id: 'ripple-auto',
        renderer: 'RIPPLE',
        zDepth: 1,
        seed: options.seed + 1,
        blendMode: 'add',
        opacity: 0.55 + profile.amplitude * 0.35,
        intensity: 0.8 + profile.bass * 0.6,
        parameters: {
          // Bass thickens and slows; treble frays.
          spawnRate: dense ? 3.2 : 1.6,
          growth: dense ? 0.65 : 0.45,
          lifetime: dense ? 2.4 : 1.6,
          distortion: 0.15 + profile.treble * 0.5,
          segments: profile.treble > 0.3 ? 96 : 48,
          hue: profile.bass > profile.treble ? 0.58 : 0.12,
        },
        signalMapping: mapping,
      }),
    );
  }

  // Text suits detail, so it arrives with the top end rather than the bottom.
  const textRenderer = available.includes('MATH')
    ? profile.treble > 0.28 && available.includes('CODE') && random.bool(0.5)
      ? 'CODE'
      : 'MATH'
    : null;

  if (budget >= 2 && profile.treble > 0.18 && textRenderer !== null) {
    layers.push(
      layer({
        id: 'text-auto',
        renderer: textRenderer,
        zDepth: 2,
        seed: options.seed + 2,
        blendMode: 'add',
        opacity: 0.35 + profile.treble * 0.4,
        parameters: { scale: 0.55, offsetX: -0.2, offsetY: 0.6 },
        signalMapping: { NOTE_MISS: { action: 'BREAK', strength: 1 } },
      }),
    );
  }

  return {
    version: VISUAL_RECIPE_VERSION,
    seed: options.seed,
    renderMode: 'AUTO',
    layers: layers.slice(0, cap),
  };
}

/**
 * Accumulates a profile while a song plays.
 *
 * Fed from the same analyser frames the signal bus uses, so this costs one
 * addition per frame and no extra audio work.
 */
export class AudioProfiler {
  private amplitude = 0;
  private bass = 0;
  private mid = 0;
  private treble = 0;
  private onsets = 0;
  private frames = 0;
  private elapsed = 0;
  private bpm = 120;

  setBpm(bpm: number): void {
    if (bpm > 0) this.bpm = bpm;
  }

  sample(
    levels: { amplitude: number; bass: number; mid: number; treble: number; onset: number },
    deltaSeconds: number,
  ): void {
    this.amplitude += levels.amplitude;
    this.bass += levels.bass;
    this.mid += levels.mid;
    this.treble += levels.treble;
    if (levels.onset > 0) this.onsets += 1;
    this.frames += 1;
    this.elapsed += Math.max(0, deltaSeconds);
  }

  get profile(): AudioProfile {
    if (this.frames === 0) return { ...QUIET_PROFILE, bpm: this.bpm };
    return {
      amplitude: this.amplitude / this.frames,
      bass: this.bass / this.frames,
      mid: this.mid / this.frames,
      treble: this.treble / this.frames,
      onsetRate: this.elapsed > 0 ? this.onsets / this.elapsed : 0,
      bpm: this.bpm,
    };
  }

  /** Whether enough has been heard for the profile to mean anything. */
  get ready(): boolean {
    return this.elapsed >= 1.5;
  }

  reset(): void {
    this.amplitude = 0;
    this.bass = 0;
    this.mid = 0;
    this.treble = 0;
    this.onsets = 0;
    this.frames = 0;
    this.elapsed = 0;
  }
}
